import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris/property-filter/interfaces";

/**
 * Definition of a single group in the property filter dropdown.
 *
 * The full grouping layout is declared here (data-driven), not in
 * the component. Adding, removing, or moving a property between
 * groups only requires editing this file.
 */
export interface PropertyFilterGroupDef {
  /** Internal identifier used as `FilteringProperty.group`. */
  readonly id: string;
  /** Heading shown to the user in the dropdown. */
  readonly label: string;
  /**
   * How properties in this group are ordered:
   * - `"preserve"` keeps them in the order listed in `properties`.
   * - `"alpha"` sorts by localized `propertyLabel`.
   */
  readonly order: "preserve" | "alpha";
  /** Property keys assigned to this group. */
  readonly properties: readonly string[];
}

export const PROPERTY_FILTER_GROUPS: readonly PropertyFilterGroupDef[] = [
  {
    id: "frequently-used",
    label: "Frequently used",
    order: "preserve",
    properties: [
      "asin",
      "announceDate",
      "onlineStreetDate",
      "programId",
      "country",
      "asinParty",
      "asinType",
    ],
  },
  {
    id: "status",
    label: "Status",
    order: "alpha",
    properties: [
      "baselineStatus",
      "buyBoxStatus",
      "validationOverallStatus",
      "validationStatus",
      "makoWeblabStatus",
    ],
  },
  {
    id: "all-properties",
    label: "All properties",
    order: "alpha",
    properties: [
      "allowListedDeviceCode",
      "allowListedDtid",
      "bomDtids",
      "bundleBaseComponent",
      "bundleComponents",
      "catalogLiveness",
      "description",
      "domainName",
      "dtcp",
      "gtin",
      "itemType",
      "liveness",
      "partNumber",
      "productLineId",
      "productionFamily",
      "salesChannel",
      "softwareOutOfBoxExperience",
      "upc",
      "userDescription",
    ],
  },
];

/**
 * Build a `filteringProperties` array ordered by the group definitions.
 *
 * - Groups appear in the order declared in `groups`.
 * - Within each group, properties are ordered per `group.order`.
 * - Every returned property carries a `group` id, matched by an entry
 *   in `customGroupsText` (see {@link buildCustomGroupsText}).
 * - Any property present in the config but not listed in any group
 *   falls into the last group (alphabetized), so a new entry in the
 *   filter config never silently disappears from the dropdown.
 */
export function buildGroupedFilteringProperties(
  properties: readonly PropertyFilterProps.FilteringProperty[],
  groups: readonly PropertyFilterGroupDef[] = PROPERTY_FILTER_GROUPS
): PropertyFilterProps.FilteringProperty[] {
  if (groups.length === 0) {
    return [...properties];
  }
  const byKey = new Map(properties.map((p) => [p.key, p]));
  const assigned = new Set<string>();
  const result: PropertyFilterProps.FilteringProperty[] = [];

  for (const group of groups) {
    const bucket: PropertyFilterProps.FilteringProperty[] = [];
    for (const key of group.properties) {
      const property = byKey.get(key);
      if (property && !assigned.has(key)) {
        bucket.push({ ...property, group: group.id });
        assigned.add(key);
      }
    }
    if (group.order === "alpha") {
      bucket.sort((a, b) => a.propertyLabel.localeCompare(b.propertyLabel));
    }
    result.push(...bucket);
  }

  const fallbackGroupId = groups[groups.length - 1].id;
  const unassigned = properties
    .filter((p) => !assigned.has(p.key))
    .sort((a, b) => a.propertyLabel.localeCompare(b.propertyLabel))
    .map((p) => ({ ...p, group: fallbackGroupId }));
  result.push(...unassigned);

  return result;
}

/**
 * `customGroupsText` entries for the given group definitions, matching
 * the `group` id assigned by {@link buildGroupedFilteringProperties}.
 */
export function buildCustomGroupsText(
  groups: readonly PropertyFilterGroupDef[] = PROPERTY_FILTER_GROUPS
): PropertyFilterProps.GroupText[] {
  return groups.map((g) => ({
    group: g.id,
    properties: g.label,
    values: `${g.label} values`,
  }));
}
